import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { PlanPickerCard } from '../MemberCreateModal/parts/PlanPickerCard';
import { LocalErrorBox } from '../MemberCreateModal/parts/LocalErrorBox';
import { SoftIcon } from '../MemberCreateModal/ui/SoftIcon';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { MembershipUsageCard } from '../MemberCreateModal/MembershipUsageCard';
import { uiBus } from 'shared/api/uiBus';
function numOrNull(x) {
  if (x === '' || x == null) return null;
  const n = Number(String(x).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function todayYmd() {
  return new Date().toISOString().slice(0, 10); // yyyy-MM-dd
}

const empty = () => ({
  planRef: '',
  customPlan: null,

  planSnapshot: null,
  planPriceTry: null,
  agreedPriceTry: '',

  startAt: null, // yyyy-MM-dd veya null
  durationMonths: 1,
  usageByItem: [],
});

export function PackageCreateModal({ open, showUsage = false, onClose, memberId, onCreated }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));

  const [form, setForm] = React.useState(empty());
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const lastPlanKeyRef = React.useRef(null);

  const buildPlanKey = (payload) => {
    if (!payload) return '';

    const name = String(payload?.name || '').trim();
    const priceTry = Number(payload?.priceTry ?? 0);
    const cap = Number(payload?.classCapacity ?? 1);

    const items = Array.isArray(payload?.items) ? payload.items : [];
    const itemsSig = items
      .map((it) => {
        const id = it?.activityId != null ? String(it.activityId) : '';
        const nm = String(it?.activityName || '')
          .trim()
          .toLowerCase();
        const s = Number(it?.sessions ?? 0);
        const m = Number(it?.makeUp ?? 0);
        const g = Number(it?.giftCount ?? 0);
        // id varsa id’yi baz al, yoksa name
        const key = id ? `id:${id}` : `nm:${nm}`;
        return `${key}:${s}:${m}:${g}`;
      })
      .join('|');

    return `${name}#${priceTry}#${cap}#${itemsSig}`;
  };
  React.useEffect(() => {
    if (!open) return;
    setForm(empty());
    setSaving(false);
    setErr(null);
  }, [open]);

  const set = React.useCallback((k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
  }, []);

  const canSave = !!form?.planSnapshot && String(form?.planSnapshot?.name || '').trim().length > 0;

  const safeClose = () => {
    if (saving) return;
    onClose?.();
  };

  const onSave = async () => {
    if (!memberId) return setErr('memberId yok');
    if (!canSave) return setErr('Paket seçmelisin (veya özel paket oluşturmalısın).');

    setSaving(true);
    setErr(null);

    try {
      const startAt = form.startAt || todayYmd();

      const usageByItem = (form.usageByItem || [])
        .filter((u) => String(u?.activityName || '').trim().length > 0)
        .map((u) => ({
          activityName: u.activityName,
          usedSessions: u.usedSessions ?? 0,
          usedMakeUp: u.usedMakeUp ?? 0,
          usedGift: u.usedGift ?? 0,
        }));

      const dto = {
        memberId,
        startAt,
        durationMonths: form.durationMonths ?? 1,

        // plan info
        planRefId: form.planRef || null,
        planSnapshot: form.planSnapshot,
        planPriceTry: numOrNull(form.planPriceTry),
        agreedPriceTry: numOrNull(form.agreedPriceTry),

        // ✅ kullanım
        usageByItem,
      };
      // ✅ DEBUG: payload’ı okunur bas
      console.log('🟡 [PackageCreateModal] create package payload:', dto);
      console.log('🟡 planSnapshot:', dto.planSnapshot);
      console.log('🟡 planSnapshot.items:', dto.planSnapshot?.items);

      await membersApi.createMemberPackage(memberId, dto);
      uiBus.emit('toast', {
        type: 'success',
        message: 'Paket başarıyla oluşturuldu',
      });
      onCreated?.();
      onClose?.();
    } catch (e) {
      const message = e?.response?.data?.message || e?.message || 'Paket oluşturulamadı';

      setErr(message);

      uiBus.emit('toast', {
        type: 'error',
        message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const headerBg = 'linear-gradient(180deg, rgba(0,150,136,.16), rgba(0,150,136,.06))';
  // component içinde (PackageCreateModal içinde)

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : safeClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={isWide ? 'md' : 'sm'}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
          width: isMobile ? '100%' : isWide ? '980px' : '820px',
          maxWidth: isMobile ? '100%' : '95vw',
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: headerBg }}>
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1.2} sx={{ minWidth: 0 }}>
              <SoftIcon icon="redeem" tone="teal" />
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  Paket Oluştur
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  Plan seç veya özel paket oluştur
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={safeClose}
              disabled={saving}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.25 }}>
          <PlanPickerCard
            open={open}
            value={form.planRef}
            onChangeValue={(v) => set('planRef', v)}
            onChangePlanPayload={(payload) => {
              setForm((prev) => {
                const nextKey = buildPlanKey(payload);
                const prevKey = lastPlanKeyRef.current;

                if (!prevKey) lastPlanKeyRef.current = nextKey;

                const planChanged = prevKey && prevKey !== nextKey;
                if (planChanged) lastPlanKeyRef.current = nextKey;

                return {
                  ...prev,
                  planSnapshot: payload ?? null,
                  planPriceTry: payload?.priceTry ?? null,
                  usageByItem: planChanged ? [] : prev.usageByItem, // ✅ artık blur’da sıfırlamaz
                };
              });
            }}
            customPlanPayload={form.customPlan}
            onChangeCustomPlanPayload={(payload) => {
              // ✅ PlanPickerCard library seçince customPlan'ı temizlemek için null gönderir.
              //    Bu durumda planSnapshot / usageByItem'e dokunma!
              if (payload == null) {
                setForm((prev) => ({ ...prev, customPlan: null }));
                return;
              }

              // ✅ custom plan gerçekten set ediliyorsa (payload dolu)
              setForm((prev) => {
                const nextKey = buildPlanKey(payload);
                const prevKey = lastPlanKeyRef.current;

                if (!prevKey) lastPlanKeyRef.current = nextKey;

                const planChanged = prevKey && prevKey !== nextKey;
                if (planChanged) lastPlanKeyRef.current = nextKey;

                return {
                  ...prev,
                  customPlan: payload,
                  planSnapshot: payload,
                  planPriceTry: payload?.priceTry ?? null,
                  usageByItem: planChanged ? [] : prev.usageByItem,
                };
              });
            }}
            agreedPriceTry={form.agreedPriceTry}
            onChangeAgreedPriceTry={(v) => set('agreedPriceTry', v)}
          />

          {/* ✅ Plan seçilince kullanım alanı açılır */}
          {/* ✅ Kullanım alanı: sadece istenirse göster */}
          {showUsage && form?.planSnapshot ? (
            <MembershipUsageCard
              planSnapshot={form.planSnapshot}
              usageByItem={form.usageByItem}
              onChangeUsage={(next) => set('usageByItem', next)}
            />
          ) : null}

          <LocalErrorBox message={err} />
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <MDButton
          variant="outlined"
          color="dark"
          sx={{ borderRadius: '14px' }}
          onClick={safeClose}
          disabled={saving}
        >
          İptal
        </MDButton>

        <MDButton
          variant="gradient"
          color="success"
          sx={{ borderRadius: '14px' }}
          disabled={!canSave || saving}
          onClick={onSave}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
          Oluştur
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
